import React, { useEffect, useState } from 'react';
import { Button, Modal, Stack, Image } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import useCommonStore from 'store/common';
import { useGetpostLikedUsersQuery } from 'services';
import InfiniteScroll from 'react-infinite-scroll-component';
import { dataLimits, S3_URL, USER_TYPES } from 'constants/index';
import { unitNumber } from 'utils';
import Loader from 'components/Common/Loader';
import useFeedsStore, { setPostLikedUsers } from 'store/feeds';
import { useQueryClient } from '@tanstack/react-query';
import useTranslate from 'hooks/useTranslate';
import { useNavigate } from 'react-router-dom';
import ConnectBtn from 'components/Common/ActionButton/ConnectBtn';
import FollowBtn from 'components/Common/ActionButton/FollowBtn';
import { updateIsConnected } from 'store/feeds';
import useAuthStore from 'store/auth';

interface User {
	userId: number;
}

const PostLikedBy: React.FC<any> = () => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const { modalConfig = {} }: any = useCommonStore((state) => state);
	const { postLikedUsers }: Record<string, any> = useFeedsStore((state) => state);
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const queryClient = useQueryClient();
	const { id } = modalConfig;
	const [currentPage, setCurrentPage] = useState(1);
	const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

	const searchString = `${id}?order=DESC&page=${currentPage}&limit=${dataLimits?.postLikedModal}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
	const {
		data: { docs: users = [], meta = {} } = {},
		status,
		isFetching,
	} = useGetpostLikedUsersQuery({
		searchString,
		currentPage,
		id,
	});

	const loadMoreFunc = () => {
		setCurrentPage((prev) => prev + 1);
	};

	useEffect(() => {
		if (currentPage > 1) queryClient.invalidateQueries({ queryKey: ['get-post-liked-users'] });
	}, [currentPage]);

	useEffect(() => {
		if (status === 'success' && users && !isFetching) {
			const existingUserIds = new Set(postLikedUsers.map((user: User) => user.userId));
			const newRecords = users.filter((user: User) => !existingUserIds.has(user.userId));

			setPostLikedUsers([...postLikedUsers, ...newRecords]);

			// Mark initial load as complete after first successful data fetch
			if (currentPage === 1) {
				setIsInitialLoadComplete(true);
			}
		}
	}, [users, status, id, isFetching]);

	const goToProfile = (user: any) => {
		navigate(`/${user?.userType}/profile/${user?.userId}`);
	};

	// Don't render until initial load is complete
	if (!isInitialLoadComplete) {
		return (
			<Modal.Body className="p-30 pe-0">
				<div
					className="d-flex justify-content-center align-items-center"
					style={{ minHeight: '200px' }}
				>
					<Loader />
				</div>
			</Modal.Body>
		);
	}

	return (
		<Modal.Body className="p-30 pe-0">
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Stack className="share-post gap-3 gap-sm-4">
				<div className="share-post-head d-flex align-items-center justify-content-between my-2">
					<h4 className="fw-bold mb-0">{translate('Modal.likedBy')}</h4>
					<h5 className="mb-0">
						{unitNumber(meta?.totalDocs, 0)} {translate('Modal.likes')}
					</h5>
				</div>

				<div id={`infiniteDiv-users-list`} className="users-list">
					<InfiniteScroll
						dataLength={postLikedUsers?.length}
						next={loadMoreFunc}
						hasMore={meta?.hasNextPage}
						loader={
							postLikedUsers?.length >= 1 && (
								<div className="d-flex justify-content-center align-items-center">
									<Loader />
								</div>
							)
						}
						scrollableTarget={`infiniteDiv-users-list`}
						scrollThreshold="300px"
					>
						{postLikedUsers?.length > 0 ? (
							postLikedUsers?.map((user: any) => (
								<div key={user?.userId} className="users-detail d-flex align-items-center gap-2">
									<div className="d-flex align-items-center gap-2 gap-xl-3 flex-1">
										<div className="users-detail-profile rounded-circle">
											<Image
												onClick={() => goToProfile(user)}
												style={{ cursor: 'pointer' }}
												src={
													user?.profilePicture
														? `${S3_URL}${user.profilePicture}`
														: IMAGE_PATHS.UserDefault
												}
												className="w-100 h-100 object-fit-cover rounded-circle"
												loading="lazy"
											/>
										</div>
										<div className="users-detail-info flex-1">
											<h6
												onClick={() => goToProfile(user)}
												style={{ cursor: 'pointer' }}
												className="fw-bold mb-1"
											>
												{user?.name}
											</h6>
											<p className="mb-0">
												{[
													user?.description,
													user?.userType === 'seeker' && user?.seekerCompanyName
														? `at ${user.seekerCompanyName}`
														: null,
												]
													.filter(Boolean)
													.join(' ')}
											</p>
										</div>
									</div>
									{Number(authUser?.userId) !== Number(user?.userId) &&
										authUser?.userType === USER_TYPES.COMPANY &&
										user?.userType === USER_TYPES.COMPANY && (
											<FollowBtn
												isFollowed={user?.isFollowed}
												id={user?.userId}
												follow={'comp'}
												show={true}
											/>
										)}

									{Number(authUser?.userId) !== Number(user?.userId) &&
										authUser?.userType === USER_TYPES.SEEKER &&
										(user?.userType === 'seeker' ? (
											<ConnectBtn
												isConnected={user?.isConnected}
												userId={user?.userId}
												btnFunc={false}
												show={true}
											/>
										) : (
											<FollowBtn
												isFollowed={user?.isFollowed}
												id={user?.userId}
												follow={'comp'}
												show={true}
											/>
										))}
								</div>
							))
						) : (
							<Stack>{translate('Common.noDataFound')}</Stack>
						)}
					</InfiniteScroll>
				</div>
			</Stack>
		</Modal.Body>
	);
};

export default PostLikedBy;
